import React, { useId } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import ChevronRightIcon from "../../icons/ChevronRight";
import { Contact } from "../../services/contacts";
import WhatsappIcon from "../Editor/pages/Sections/icons/Whatsapp";
import { Actions, CopyButton, Favicon, Info, Meta, TitleRow } from "../Links/components/LinkCard/styles";
import { contactDate, emailOf, phoneOf, whatsappLink } from "./helpers";
import { ContactFrame } from "./styles";

const prefix = "pages.contacts";

const MailIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.8" />
    <path d="m4 7 8 6 8-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const stop = (event: React.SyntheticEvent) => event.stopPropagation();

type Props = {
  contact: Contact;
  /** The contact's screen. */
  to: string;
};

/**
 * A contact in the list, drawn as a link in Keepo Links (opened with a click anywhere on it): who sent it, their
 * e-mail and phone and when it arrived; and, at the end, answering them on WhatsApp or by e-mail.
 */
export const ContactCard = ({ contact, to }: Props) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const titleId = useId();
  const title = contact.title || t(`${prefix}.untitled`);
  const email = emailOf(contact);
  const phone = phoneOf(contact);
  const whatsapp = phone ? whatsappLink(phone) : null;
  const ways = [email, phone].filter(Boolean) as string[];

  return (
    <ContactFrame aria-labelledby={titleId} onClick={() => navigate(to)}>
      <Favicon aria-hidden="true" className="initial">
        {title.trim().charAt(0).toUpperCase()}
      </Favicon>

      <Info>
        <TitleRow>
          <h4>
            <Link id={titleId} to={to} onClick={stop}>
              {title}
            </Link>
          </h4>
        </TitleRow>
        {ways.length > 0 && (
          <p className="url">
            <span className="short">{ways[0]}</span>
            {ways[1] && <span> · {ways[1]}</span>}
          </p>
        )}
        {contact.createdAt && (
          <Meta>
            <span>{t(`${prefix}.receivedAt`, { date: contactDate(contact.createdAt, i18n.language) })}</span>
          </Meta>
        )}
      </Info>

      <Actions onClick={stop}>
        {whatsapp && (
          <CopyButton
            as="a"
            href={whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t(`${prefix}.answerWhatsapp`, { name: title })}
            title={t(`${prefix}.answerWhatsapp`, { name: title })}>
            <WhatsappIcon width={20} height={20} />
          </CopyButton>
        )}
        {email && (
          <CopyButton
            as="a"
            href={`mailto:${email}`}
            aria-label={t(`${prefix}.answerEmail`, { name: title })}
            title={t(`${prefix}.answerEmail`, { name: title })}>
            <MailIcon />
          </CopyButton>
        )}
        <span className="chevron">
          <ChevronRightIcon width={14} height={14} color="currentColor" />
        </span>
      </Actions>
    </ContactFrame>
  );
};
